import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Locale = 'it' | 'en';

type TranslationValue = string;
type TranslationDict = Record<string, TranslationValue>;

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, fallback?: string) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const translations: Record<Locale, TranslationDict> = {
  it: {
    'nav.features': 'Features',
    'nav.plugins': 'Plugins',
    'nav.premium': 'Premium',
    'nav.support': 'Support',
    'nav.dashboard': 'Dashboard',
    'nav.login': 'Login',
    'nav.addDiscord': 'Aggiungi a Discord',
    'nav.logout': 'Logout',
    'nav.notifications': 'Notifiche',
    'nav.noNotifications': 'Nessuna notifica',
    'nav.language': 'Lingua',

    'footer.description': 'Il bot Discord tutto-in-uno per moderazione, engagement e automazioni della tua community.',
    'footer.product': 'Prodotto',
    'footer.resources': 'Risorse',
    'footer.documentation': 'Documentazione',
    'footer.apiReference': 'Riferimento API',
    'footer.status': 'Stato del servizio',
    'footer.changelog': 'Changelog',
    'footer.support': 'Supporto',
    'footer.helpCenter': 'Centro assistenza',
    'footer.discordServer': 'Server Discord',
    'footer.contactUs': 'Contattaci',
    'footer.reportBug': 'Segnala un bug',
    'footer.rights': 'Tutti i diritti riservati.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Termini di Servizio',
    'footer.cookies': 'Cookie Policy',
    'footer.madeWith': 'Fatto con',
    'footer.forDiscord': 'per Discord',

    'hero.trustedBy': 'Scelto da {{count}} server',
    'hero.titleTop': 'Il #1',
    'hero.titleBottom': 'Discord Bot',
    'hero.subtitle': 'Fai crescere e gestisci il tuo server Discord con leveling, moderazione, comandi personalizzati e molto altro. Scelto da milioni di community nel mondo.',
    'hero.addDiscord': 'Aggiungi a Discord',
    'hero.watchDemo': 'Guarda Demo',
    'hero.servers': '{{count}} server',
    'hero.online': 'Online',
    'hero.offline': 'Offline',
    'hero.automodActive': 'Auto-Mod Attivo',
    'hero.threatsBlocked': '{{count}} minacce bloccate',
    'hero.levelUp': 'Level Up!',
    'hero.levelReached': '@user ha raggiunto il livello 25',

    'features.titleAccent': 'Features',
    'features.title': 'complete',
    'features.subtitle': 'Tutto quello che serve per far crescere e proteggere la tua community.',
    'features.item.moderation.title': 'Moderazione intelligente',
    'features.item.moderation.desc': 'Auto-mod, filtri e strumenti avanzati per mantenere il server sicuro.',
    'features.item.automation.title': 'Automazioni rapide',
    'features.item.automation.desc': 'Flussi automatici con trigger, condizioni e azioni personalizzate.',
    'features.item.engagement.title': 'Engagement community',
    'features.item.engagement.desc': 'Leveling, sondaggi e giochi per aumentare la partecipazione.',
    'features.item.config.title': 'Configurazione completa',
    'features.item.config.desc': 'Dashboard unica per gestire plugin, permessi e impostazioni.',
    'features.item.ai.title': 'Strumenti AI',
    'features.item.ai.desc': 'Assistenti intelligenti per risposte rapide e contenuti dinamici.',
    'features.item.analytics.title': 'Analytics in tempo reale',
    'features.item.analytics.desc': 'Metriche e log per controllare crescita e attività del server.',

    'premium.title': 'Premium Demo',
    'premium.subtitle': 'Moduli premium in anteprima con AI, sicurezza avanzata e supporto evoluto.',
    'premium.cta': 'Scopri Premium',
    'premium.item.aiIntegration.title': 'Integrazione AI (Gemini)',
    'premium.item.aiIntegration.desc': 'Tono e linguaggio personalizzati con modelli Gemini.',
    'premium.item.image.title': 'AI Text-to-Image',
    'premium.item.image.desc': 'Generazione immagini per annunci e post.',
    'premium.item.tickets.title': 'Ticket System avanzato',
    'premium.item.tickets.desc': 'Supporto unico AI/umano con workflow dedicati.',
    'premium.item.security.title': 'Sicurezza Pro',
    'premium.item.security.desc': 'Anti-raid avanzato + blacklist server condivisi.',
    'premium.item.automation.title': 'Automation Boost',
    'premium.item.automation.desc': 'Priorità esecuzione e analytics avanzati.',

    'support.titleAccent': 'Supporto',
    'support.title': 'dedicato',
    'support.subtitle': 'Siamo qui per aiutarti: risorse, community e supporto diretto.',
    'support.item.help.title': 'Help Center',
    'support.item.help.desc': 'Guide rapide e risposte alle domande più comuni.',
    'support.item.help.action': 'Apri Help Center',
    'support.item.discord.title': 'Discord Community',
    'support.item.discord.desc': 'Unisciti al server ufficiale per supporto e news.',
    'support.item.discord.action': 'Entra su Discord',
    'support.item.contact.title': 'Contattaci',
    'support.item.contact.desc': 'Richiedi assistenza personalizzata per la tua community.',
    'support.item.contact.action': 'Invia richiesta',
    'support.item.bug.title': 'Segnala un bug',
    'support.item.bug.desc': 'Aiutaci a migliorare segnalando problemi e bug.',
    'support.item.bug.action': 'Segnala',

    'footer.description': 'Il bot Discord più completo per gestione community, engagement e crescita.',
    'footer.product': 'Prodotto',
    'footer.resources': 'Risorse',
    'footer.support': 'Supporto',
    'footer.documentation': 'Documentazione',
    'footer.apiReference': 'Riferimento API',
    'footer.status': 'Stato',
    'footer.changelog': 'Changelog',
    'footer.helpCenter': 'Help Center',
    'footer.discordServer': 'Server Discord',
    'footer.contactUs': 'Contattaci',
    'footer.reportBug': 'Segnala Bug',
    'footer.rights': 'Tutti i diritti riservati.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Termini di Servizio',
    'footer.cookies': 'Cookie Policy',
    'footer.madeWith': 'Creato con',
    'footer.forDiscord': 'per Discord',

    'dashboard.server': 'Server',
    'dashboard.accessDenied': 'Accesso negato',
    'dashboard.blacklistReason': 'Sei membro di server in blacklist e non puoi accedere alla dashboard.',
    'dashboard.blacklistedServers': 'Server in blacklist:',
    'dashboard.reason': 'Motivo',
    'dashboard.blacklistRecovery': 'Per riottenere l\'accesso devi uscire da tutti i server in blacklist indicati sopra. Poi effettua di nuovo il login.',
    'dashboard.totalMembers': 'Membri totali',
    'dashboard.activePlugins': 'Plugin attivi',
    'dashboard.serverName': 'Nome server',
    'dashboard.serverId': 'ID server',
    'dashboard.unknown': 'Sconosciuto',
    'dashboard.serverInfo': 'Informazioni server',
    'dashboard.members': 'Membri',
    'dashboard.icon': 'Icona',
    'dashboard.pluginsStatus': 'Stato plugin',
    'dashboard.noActivePlugins': 'Nessun plugin attivo',
    'dashboard.overview': 'Panoramica dashboard',
    'dashboard.dataWhenActive': 'I dati verranno mostrati qui una volta che il bot sarà attivo',
    'dashboard.botMissing': 'Bot non presente nel server',
    'dashboard.inviteNeeded': 'Devi invitare il bot Discord al server prima di poter accedere alla dashboard.',
    'dashboard.inviteAfter': 'Una volta invitato il bot, potrai gestire tutte le funzionalità del server.',
    'dashboard.inviteButton': 'Invita bot al server',

    'accepted.title': 'Accepted Members',
    'accepted.subtitle': 'Approva o rifiuta nuovi membri con dashboard e logica pending.',
    'accepted.pluginToggle': 'Plugin Accepted Members',
    'accepted.flowTitle': 'Abilita flusso Accepted Members',
    'accepted.flowDesc': 'Invia richiesta in canale admin con bottoni Approva/Rifiuta.',
    'accepted.adminChannel': 'Canale admin',
    'accepted.verifiedRole': 'Ruolo verificato',
    'accepted.none': 'Nessuno',
    'accepted.timeoutSeconds': 'Timeout (secondi)',
    'accepted.declineReason': 'Motivo rifiuto predefinito',
    'accepted.sendWelcome': 'Invia welcome dopo approvazione',
    'accepted.sendWelcomeDesc': 'Se attivo, il messaggio di benvenuto viene inviato solo dopo approvazione.',
    'accepted.dashboardHint': 'La dashboard pending/analytics viene aggiornata automaticamente nel canale admin.',
    'accepted.save': 'Salva modifiche',
    'accepted.savedToast': 'Accepted Members configurato!',
    'accepted.pluginUpdatedToast': 'Plugin Accepted Members aggiornato',
    'accepted.selectChannel': 'Seleziona canale',
    'accepted.selectRole': 'Seleziona ruolo',

    'verification.title': 'Verifica',
    'verification.subtitle': 'Verifica utenti con captcha e metodi alternativi.',
    'verification.unavailable': 'Plugin non disponibile per questo server.',
    'verification.embedStatus': 'Stato embed verifica:',
    'verification.loading': 'Caricamento...',
    'verification.spawned': '✅ Spawnato in chat',
    'verification.notSpawned': '❌ Non spawnato',
    'verification.refresh': 'Aggiorna stato',
    'verification.openEmbed': 'Apri messaggio embed',
    'verification.channel': 'Canale verifica',
    'verification.verifiedRole': 'Ruolo verificato',
    'verification.unverifiedRole': 'Ruolo non verificato (opzionale)',
    'verification.provider': 'Provider captcha',
    'verification.maxAttempts': 'Max tentativi',
    'verification.timeoutMinutes': 'Timeout (minuti)',
    'verification.methods': 'Metodi di verifica',
    'verification.methodButton': 'Pulsante Verify',
    'verification.methodCaptcha': 'Captcha',
    'verification.methodEmail': 'Verifica Email',
    'verification.methodOAuth': 'OAuth Discord',
    'verification.captchaEnabled': 'Captcha abilitato',
    'verification.captchaEnabledDesc': 'Richiede risoluzione captcha prima del ruolo',
    'verification.autoKick': 'Kick automatico non verificati',
    'verification.autoKickDesc': 'Rimuove utenti non verificati allo scadere del timeout',
    'verification.publishEmbed': 'Pubblica embed verifica',
    'verification.save': 'Salva modifiche',
    'verification.savedToast': 'Configurazione salvata!',
    'verification.publishedToast': 'Embed verifica pubblicato con successo!',
    'verification.publishError': 'Errore pubblicazione embed verifica',
    'verification.providerHelp': 'Per hCaptcha/Turnstile è richiesto che il backend abbia site key + secret configurati.',
    'verification.selectChannel': 'Seleziona canale verifica',
    'verification.selectRoleVerified': 'Seleziona ruolo verificato',
    'verification.selectRoleUnverified': 'Seleziona ruolo non verificato',
    'verification.selectProvider': 'Seleziona provider',
    'verification.noneChannel': 'Nessun canale',
    'verification.noneRole': 'Nessun ruolo',

    'servers.loading': 'Caricamento server...',
    'servers.error': 'Errore caricamento server',
    'servers.retry': 'Riprova',
    'servers.title': 'I miei server',
    'servers.logout': 'Logout',
    'servers.noServers': 'Nessun server disponibile',
    'servers.noServersDesc': 'Devi essere proprietario o avere permessi amministratore per gestire un server.',
    'servers.onlyBot': 'Solo server con bot',
    'servers.showAll': 'Mostra tutti',
    'servers.welcome': 'Benvenuto, {{username}}!',
    'servers.selectPrompt': 'Seleziona un server per iniziare a gestire i plugin',
    'servers.members': '{{count}} membri',

    'management.title': 'Gestione server',
    'management.server': 'Server',
    'management.toggleUpdated': 'Stato plugin aggiornato',
    'management.toggleError': 'Errore aggiornamento plugin',
    'management.loading': 'Caricamento...',
    'management.enable': 'Attiva',
    'management.disable': 'Disattiva',
    'management.configure': 'Configura',
    'management.enabled': 'Abilitato',
    'management.disabled': 'Disabilitato',
    'management.all': 'Tutti',
    'management.overview': 'Panoramica',
    'management.overviewDesc': 'Visualizza statistiche generali e lo stato dei plugin del server',
    'management.welcomeDesc': 'Messaggi di benvenuto personalizzati per nuovi membri',
    'management.acceptedDesc': 'Approva/rifiuta nuovi membri con dashboard pending e analytics',
    'management.verificationDesc': 'Captcha + metodi di verifica multipli',

    'welcome.loadError': 'Errore caricamento plugin',
    'welcome.unableLoad': 'Impossibile caricare i dati',
    'welcome.loadingConfig': 'Caricamento configurazione plugin...',
    'welcome.title': 'Benvenuto & Addio',
    'welcome.subtitle': 'Dai il benvenuto ai nuovi membri e registra chi lascia il server',
    'welcome.pluginStatus': 'Stato plugin',
    'welcome.disabled': 'Plugin disattivato',
    'welcome.disabledDesc': 'Attiva il plugin per inviare messaggi di benvenuto e ruolo automatico',
    'welcome.enableNow': 'Attiva ora',
    'welcome.saveFailed': 'Salvataggio fallito',
    'welcome.saveFailedDesc': 'Si è verificato un errore durante il salvataggio. Controlla la connessione.',
    'welcome.welcomeMessage': 'Messaggio di benvenuto',
    'welcome.sendOnJoin': 'Invia messaggio all\'ingresso',
    'welcome.channel': 'Canale',
    'welcome.systemChannel': 'Canale di sistema',
    'welcome.autoRole': 'Ruolo automatico',
    'welcome.none': 'Nessuno',
    'welcome.messageContent': 'Contenuto messaggio',
    'welcome.sendAsEmbed': 'Invia come embed',
    'welcome.embedTitle': 'Titolo embed',
    'welcome.embedColor': 'Colore embed',
    'welcome.embedThumb': 'Miniatura embed',
    'welcome.userAvatar': 'Avatar utente',
    'welcome.serverIcon': 'Icona server',
    'welcome.noThumb': 'Nessuna',
    'welcome.embedBanner': 'Banner embed',
    'welcome.showServerBanner': 'Mostra banner server',
    'welcome.customBanner': 'Banner personalizzato',
    'welcome.bannerReminder': 'Ricorda: il banner sarà usato solo dopo aver salvato la configurazione.',
    'welcome.bannerError': 'Errore caricamento banner',
    'welcome.change': 'Cambia',
    'welcome.uploadBanner': 'Clicca per caricare il banner',
    'welcome.uploading': 'Caricamento in corso...',
    'welcome.uploadHint': 'PNG / JPG • max 5MB • consigliato 1200×400',
    'welcome.farewellMessage': 'Messaggio di addio',
    'welcome.sendOnLeave': 'Invia messaggio quando un utente lascia',
    'welcome.noPreview': 'Nessuna anteprima disponibile. Carica un banner.',
    'welcome.uploadBannerBtn': 'Carica banner',
    'welcome.saving': 'Salvataggio...',
    'welcome.save': 'Salva modifiche',

    'support.helpCenter.title': 'Help Center',
    'support.helpCenter.subtitle': 'Guide rapide e risposte alle domande più comuni. Qui trovi FAQ, setup iniziale e best practice.',
    'support.helpCenter.quickGuide': 'Guida rapida',
    'support.helpCenter.faq': 'FAQ',
    'support.helpCenter.discordSetup': 'Setup Discord',
    'support.helpCenter.needSupport': 'Serve supporto?',
    'support.helpCenter.needSupportDesc': 'Contattaci o unisciti alla community per assistenza rapida.',
    'support.helpCenter.communityDiscord': 'Community Discord',
    'support.helpCenter.sendRequest': 'Invia richiesta',

    'support.contact.title': 'Contattaci',
    'support.contact.subtitle': 'Richiedi assistenza personalizzata per la tua community. Ti risponderemo al più presto.',
    'support.contact.name': 'Nome',
    'support.contact.namePlaceholder': 'Il tuo nome',
    'support.contact.email': 'Email',
    'support.contact.subject': 'Oggetto',
    'support.contact.subjectPlaceholder': 'Titolo richiesta',
    'support.contact.message': 'Messaggio',
    'support.contact.messagePlaceholder': 'Descrivi la tua richiesta',
    'support.contact.send': 'Invia richiesta',

    'support.bug.titlePage': 'Segnala un bug',
    'support.bug.subtitlePage': 'Aiutaci a migliorare segnalando problemi e bug riscontrati.',
    'support.bug.pageModule': 'Pagina/Modulo',
    'support.bug.pageModulePlaceholder': 'Dove hai trovato il bug',
    'support.bug.description': 'Descrizione',
    'support.bug.descriptionPlaceholder': 'Descrivi il problema',
    'support.bug.send': 'Segnala',

    'architect.updated': 'AI Server Architect aggiornato',
    'architect.operationCompleted': 'Operazione completata.',
    'architect.chatError': 'Errore chat AI',
    'architect.enableFirst': 'Attiva prima il plugin AI Server Architect',
    'architect.noBlueprint': 'Nessun blueprint disponibile',
    'architect.simulationCompleted': 'Simulazione completata',
    'architect.appliedToDiscord': 'Modifiche applicate su Discord',
    'architect.streamError': 'Errore stream apply',
    'architect.applyStreamingError': 'Errore apply streaming',
    'architect.subtitle': 'Agente AI Gemini per creare, correggere e ottimizzare server Discord da zero.',
    'architect.chatTitle': 'Chat AI',
    'architect.chatDesc': 'Descrivi come vuoi il server e l’agente genera il blueprint completo con Gemini.',
    'architect.goalsPlaceholder': 'Obiettivi: community gaming, staff hierarchy, support tickets, onboarding...',
    'architect.destructiveTitle': 'Modalità distruttiva',
    'architect.destructiveDesc': 'Permette rimozione ruoli/canali/categorie non previsti',
    'architect.emptyChat': 'Scrivi una richiesta: “crea server completo con onboarding, ruoli staff, support e log”.',
    'architect.ai': 'AI',
    'architect.you': 'Tu',
    'architect.inputPlaceholder': 'Es: crea un server completo per supporto SaaS in italiano',
    'architect.liveTitle': 'Cambiamenti Live',
    'architect.liveDesc': 'Vista in tempo reale della struttura server e piano modifiche generato dall’agente.',
    'architect.roles': 'Ruoli',
    'architect.categories': 'Categorie',
    'architect.channels': 'Canali',
    'architect.plannedChanges': 'Cambi previsti',
    'architect.refreshLive': 'Aggiorna live',
    'architect.simulate': 'Simula',
    'architect.applyDiscord': 'Applica su Discord',
    'architect.liveApplyLog': 'Log live apply',
    'architect.noLiveEvents': 'Nessun evento live ancora.',
    'architect.planTitle': 'Piano modifiche',
    'architect.overwrites': 'Overwrites',
    'architect.resultSimulation': 'Risultato simulazione',
    'architect.resultApplication': 'Risultato applicazione',
    'architect.summary': 'Ruoli creati {{rolesCreated}}, aggiornati {{rolesUpdated}}, eliminati {{rolesDeleted}} • Categorie create {{categoriesCreated}}, eliminate {{categoriesDeleted}} • Canali creati {{channelsCreated}}, eliminati {{channelsDeleted}}',

    'botPersonalizer.saved': 'Configurazione salvata!',
    'botPersonalizer.unavailable': 'Plugin non disponibile per questo server.',
    'botPersonalizer.subtitle': 'Nome, status e presenza del bot.',
    'botPersonalizer.nickname': 'Nickname (server)',
    'botPersonalizer.status': 'Status',
    'botPersonalizer.selectStatus': 'Seleziona status',
    'botPersonalizer.idle': 'Assente',
    'botPersonalizer.dnd': 'Non disturbare',
    'botPersonalizer.activity': 'Attività',
    'botPersonalizer.activityPlaceholder': 'in streaming su...',
    'botPersonalizer.save': 'Salva modifiche',

    'customCommands.subtitle': 'Crea comandi personalizzati per il server.',
    'birthdays.saved': 'Configurazione salvata!',
    'birthdays.subtitle': 'Festeggia i compleanni dei membri con annunci automatici.',
    'birthdays.announcementSettings': 'Impostazioni annunci',
    'birthdays.announcementChannel': 'Canale annunci',
    'birthdays.selectAnnouncementChannel': 'Seleziona canale annunci compleanni',
    'birthdays.noChannel': 'Nessun canale',
    'birthdays.announcementChannelHelp': 'Canale in cui verranno pubblicati gli annunci di compleanno.',
    'birthdays.announcementMessage': 'Messaggio annuncio',
    'birthdays.announcementMessagePlaceholder': 'Usa {user} per la menzione, {age} per l’età',
    'birthdays.announcementMessageHelp': 'Usa {user} per menzionare l’utente, {age} per la sua età.',
    'birthdays.announcementTime': 'Orario annuncio',
    'birthdays.reminderDaysBefore': 'Giorni di anticipo promemoria',
    'birthdays.sameDay': 'Solo giorno stesso',
    'birthdays.oneDayBefore': '1 giorno prima',
    'birthdays.threeDaysBefore': '3 giorni prima',
    'birthdays.oneWeekBefore': '1 settimana prima',
    'birthdays.pingUser': 'Menziona utente nel compleanno',
    'birthdays.pingUserDesc': 'Menziona l’utente nell’annuncio',
    'birthdays.showAge': 'Mostra età',
    'birthdays.showAgeDesc': 'Mostra l’età del membro nell’annuncio',
    'birthdays.privateReminders': 'Promemoria privati',
    'birthdays.privateRemindersDesc': 'Invia promemoria DM ai membri che compiono gli anni',
    'birthdays.availableCommands': 'Comandi disponibili',
    'birthdays.setBirthday': 'Imposta il tuo compleanno',
    'birthdays.checkBirthday': 'Controlla il compleanno di qualcuno',
    'birthdays.upcomingBirthdays': 'Visualizza i prossimi compleanni nel server',
    'birthdays.saving': 'Salvataggio...',
    'birthdays.save': 'Salva configurazione',
    'recording.updated': 'Plugin Voice Recording aggiornato',
    'recording.updateError': 'Errore aggiornamento plugin Voice Recording',
    'recording.configSaved': 'Configurazione salvata',
    'recording.configSaveError': 'Errore salvataggio configurazione',
    'recording.recordingLive': 'Registrazione...',
    'recording.authRequired': 'Autenticazione richiesta',
    'recording.downloadError': 'Errore download',
    'recording.downloaded': 'Trascrizione scaricata',
    'recording.downloadFailed': 'Download trascrizione fallito',
    'recording.deleted': 'Registrazione eliminata',
    'recording.deleteFailed': 'Eliminazione registrazione fallita',
    'recording.title': 'Voice Recording',
    'recording.subtitle': 'Registra canali vocali e trascrivi con Gemini AI',
    'recording.settings': 'Impostazioni',
    'recording.recordings': 'Registrazioni',
    'recording.geminiTitle': 'Configurazione API Gemini',
    'recording.geminiDesc': 'Configura Gemini per la trascrizione voce-testo con diarizzazione speaker',
    'recording.geminiKey': 'Gemini API Key',
    'recording.geminiHelp': 'Obbligatoria per la trascrizione speech-to-text. Ottieni la chiave da Google AI Studio.',
    'recording.speakerDiarization': 'Diarizzazione speaker',
    'recording.speakerDiarizationDesc': 'Identifica e separa diversi speaker nella trascrizione',
    'recording.autoRecordTitle': 'Canali auto-record',
    'recording.autoRecordDesc': 'Seleziona i canali vocali da registrare automaticamente quando entra qualcuno',
    'recording.noVoiceChannels': 'Nessun canale vocale trovato',
    'recording.autoStart': 'Avvio registrazione automatico',
    'recording.autoStartDesc': 'Avvia automaticamente la registrazione quando qualcuno entra nei canali selezionati',
    'recording.storageTitle': 'Storage e retention',
    'recording.storageDesc': 'Configura per quanto tempo conservare registrazioni e trascrizioni',
    'recording.retentionDays': 'Periodo retention (giorni)',
    'recording.retentionHelp': 'Le registrazioni più vecchie verranno eliminate automaticamente',
    'recording.saving': 'Salvataggio...',
    'recording.save': 'Salva configurazione',
    'recording.recentTitle': 'Registrazioni recenti',
    'recording.recentDesc': 'Visualizza e gestisci le registrazioni dei canali vocali',
    'recording.view': 'Apri',
    'recording.deleteConfirm': 'Eliminare questa registrazione?',
    'recording.participants': 'partecipanti',
    'recording.segments': 'segmenti',
    'recording.started': 'Avviata:',
    'recording.empty': 'Nessuna registrazione ancora. Usa /start-recording su Discord per iniziare.',
    'recording.transcriptionTitle': 'Trascrizione',
    'recording.close': 'Chiudi',
    'recording.staff': '👮 Staff',
    'recording.user': '👤 Utente',
    'recording.noTranscription': 'Nessuna trascrizione disponibile. Verifica che la Gemini API key sia configurata.',
    'recording.howItWorks': 'Come funziona',
    'recording.step1Title': 'Configura canali',
    'recording.step1Desc': 'Seleziona i canali vocali da monitorare e abilita auto-record se desiderato',
    'recording.step2Title': 'Avvia registrazione',
    'recording.step2Desc': 'Usa il comando /start-recording oppure lascia che l’auto-record gestisca tutto all’ingresso utenti',
    'recording.step3Title': 'Trascrizione AI',
    'recording.step3Desc': 'Gemini API trascrive il parlato in testo con diarizzazione speaker',
    'recording.step4Title': 'Visualizza e scarica',
    'recording.step4Desc': 'Accedi alle trascrizioni nella dashboard con etichette staff/utente e timestamp',
  },
  en: {
    'nav.features': 'Features',
    'nav.plugins': 'Plugins',
    'nav.premium': 'Premium',
    'nav.support': 'Support',
    'nav.dashboard': 'Dashboard',
    'nav.login': 'Login',
    'nav.addDiscord': 'Add to Discord',
    'nav.logout': 'Logout',
    'nav.notifications': 'Notifications',
    'nav.noNotifications': 'No notifications yet',
    'nav.language': 'Language',

    'footer.description': 'The all-in-one Discord bot for moderation, engagement, and automations for your community.',
    'footer.product': 'Product',
    'footer.resources': 'Resources',
    'footer.documentation': 'Documentation',
    'footer.apiReference': 'API Reference',
    'footer.status': 'Status',
    'footer.changelog': 'Changelog',
    'footer.support': 'Support',
    'footer.helpCenter': 'Help Center',
    'footer.discordServer': 'Discord Server',
    'footer.contactUs': 'Contact Us',
    'footer.reportBug': 'Report a Bug',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.cookies': 'Cookie Policy',
    'footer.madeWith': 'Made with',
    'footer.forDiscord': 'for Discord',

    'hero.trustedBy': 'Trusted by {{count}} servers',
    'hero.titleTop': 'The #1',
    'hero.titleBottom': 'Discord Bot',
    'hero.subtitle': 'Grow and manage your Discord server with leveling, moderation, custom commands, and much more. Trusted by millions of communities worldwide.',
    'hero.addDiscord': 'Add to Discord',
    'hero.watchDemo': 'Watch Demo',
    'hero.servers': '{{count}} servers',
    'hero.online': 'Online',
    'hero.offline': 'Offline',
    'hero.automodActive': 'Auto-Mod Active',
    'hero.threatsBlocked': '{{count}} threats blocked',
    'hero.levelUp': 'Level Up!',
    'hero.levelReached': '@user reached level 25',

    'features.titleAccent': 'Features',
    'features.title': 'complete',
    'features.subtitle': 'Everything you need to grow and protect your community.',
    'features.item.moderation.title': 'Smart moderation',
    'features.item.moderation.desc': 'Auto-mod, filters, and advanced tools to keep your server safe.',
    'features.item.automation.title': 'Fast automations',
    'features.item.automation.desc': 'Automatic flows with custom triggers, conditions, and actions.',
    'features.item.engagement.title': 'Community engagement',
    'features.item.engagement.desc': 'Leveling, polls, and games to increase participation.',
    'features.item.config.title': 'Complete configuration',
    'features.item.config.desc': 'Single dashboard to manage plugins, permissions, and settings.',
    'features.item.ai.title': 'AI tools',
    'features.item.ai.desc': 'Smart assistants for quick responses and dynamic content.',
    'features.item.analytics.title': 'Real-time analytics',
    'features.item.analytics.desc': 'Metrics and logs to monitor growth and activity.',

    'premium.title': 'Premium Demo',
    'premium.subtitle': 'Preview premium modules with AI, advanced security, and enhanced support.',
    'premium.cta': 'Explore Premium',
    'premium.item.aiIntegration.title': 'AI Integration (Gemini)',
    'premium.item.aiIntegration.desc': 'Custom tone and language with Gemini models.',
    'premium.item.image.title': 'AI Text-to-Image',
    'premium.item.image.desc': 'Image generation for announcements and posts.',
    'premium.item.tickets.title': 'Advanced Ticket System',
    'premium.item.tickets.desc': 'Unified AI/human support with dedicated workflows.',
    'premium.item.security.title': 'Pro Security',
    'premium.item.security.desc': 'Advanced anti-raid + shared server blacklist.',
    'premium.item.automation.title': 'Automation Boost',
    'premium.item.automation.desc': 'Priority execution and advanced analytics.',

    'support.titleAccent': 'Support',
    'support.title': 'dedicated',
    'support.subtitle': 'We are here to help: resources, community, and direct support.',
    'support.item.help.title': 'Help Center',
    'support.item.help.desc': 'Quick guides and answers to common questions.',
    'support.item.help.action': 'Open Help Center',
    'support.item.discord.title': 'Discord Community',
    'support.item.discord.desc': 'Join the official server for support and news.',
    'support.item.discord.action': 'Join Discord',
    'support.item.contact.title': 'Contact Us',
    'support.item.contact.desc': 'Request personalized support for your community.',
    'support.item.contact.action': 'Send request',
    'support.item.bug.title': 'Report a bug',
    'support.item.bug.desc': 'Help us improve by reporting issues and bugs.',
    'support.item.bug.action': 'Report',

    'footer.description': 'The most complete Discord bot for community management, engagement, and growth.',
    'footer.product': 'Product',
    'footer.resources': 'Resources',
    'footer.support': 'Support',
    'footer.documentation': 'Documentation',
    'footer.apiReference': 'API Reference',
    'footer.status': 'Status',
    'footer.changelog': 'Changelog',
    'footer.helpCenter': 'Help Center',
    'footer.discordServer': 'Discord Server',
    'footer.contactUs': 'Contact Us',
    'footer.reportBug': 'Report Bug',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.cookies': 'Cookie Policy',
    'footer.madeWith': 'Made with',
    'footer.forDiscord': 'for Discord',

    'dashboard.server': 'Server',
    'dashboard.accessDenied': 'Access Denied',
    'dashboard.blacklistReason': 'You are currently a member of blacklisted server(s) and cannot access this dashboard.',
    'dashboard.blacklistedServers': 'Blacklisted Servers:',
    'dashboard.reason': 'Reason',
    'dashboard.blacklistRecovery': 'To regain access, you must leave all blacklisted servers above. Once you\'ve removed yourself, please try logging in again.',
    'dashboard.totalMembers': 'Total Members',
    'dashboard.activePlugins': 'Active Plugins',
    'dashboard.serverName': 'Server Name',
    'dashboard.serverId': 'Server ID',
    'dashboard.unknown': 'Unknown',
    'dashboard.serverInfo': 'Server Information',
    'dashboard.members': 'Members',
    'dashboard.icon': 'Icon',
    'dashboard.pluginsStatus': 'Plugins Status',
    'dashboard.noActivePlugins': 'No active plugins',
    'dashboard.overview': 'Dashboard Overview',
    'dashboard.dataWhenActive': 'Data will be shown here once the bot is active',
    'dashboard.botMissing': 'Bot not in server',
    'dashboard.inviteNeeded': 'You need to invite the Discord bot to this server before accessing the dashboard.',
    'dashboard.inviteAfter': 'Once the bot is invited, you can manage all server features.',
    'dashboard.inviteButton': 'Invite bot to server',

    'accepted.title': 'Accepted Members',
    'accepted.subtitle': 'Approve or decline new members with pending dashboard flow.',
    'accepted.pluginToggle': 'Accepted Members Plugin',
    'accepted.flowTitle': 'Enable Accepted Members Flow',
    'accepted.flowDesc': 'Send request to admin channel with Approve/Decline buttons.',
    'accepted.adminChannel': 'Admin channel',
    'accepted.verifiedRole': 'Verified role',
    'accepted.none': 'None',
    'accepted.timeoutSeconds': 'Timeout (seconds)',
    'accepted.declineReason': 'Default decline reason',
    'accepted.sendWelcome': 'Send welcome after accept',
    'accepted.sendWelcomeDesc': 'If enabled, welcome message is sent only after approval.',
    'accepted.dashboardHint': 'Pending/analytics dashboard is automatically updated in admin channel.',
    'accepted.save': 'Save changes',
    'accepted.savedToast': 'Accepted Members configured!',
    'accepted.pluginUpdatedToast': 'Accepted Members plugin updated',
    'accepted.selectChannel': 'Select channel',
    'accepted.selectRole': 'Select role',

    'verification.title': 'Verification',
    'verification.subtitle': 'Verify users with captcha and alternative methods.',
    'verification.unavailable': 'Plugin not available for this server.',
    'verification.embedStatus': 'Verification embed status:',
    'verification.loading': 'Loading...',
    'verification.spawned': '✅ Spawned in chat',
    'verification.notSpawned': '❌ Not spawned',
    'verification.refresh': 'Refresh status',
    'verification.openEmbed': 'Open embed message',
    'verification.channel': 'Verification channel',
    'verification.verifiedRole': 'Verified role',
    'verification.unverifiedRole': 'Unverified role (optional)',
    'verification.provider': 'Captcha provider',
    'verification.maxAttempts': 'Max attempts',
    'verification.timeoutMinutes': 'Timeout (minutes)',
    'verification.methods': 'Verification methods',
    'verification.methodButton': 'Verify button',
    'verification.methodCaptcha': 'Captcha',
    'verification.methodEmail': 'Email verification',
    'verification.methodOAuth': 'Discord OAuth',
    'verification.captchaEnabled': 'Captcha enabled',
    'verification.captchaEnabledDesc': 'Requires captcha solve before assigning role',
    'verification.autoKick': 'Auto-kick unverified users',
    'verification.autoKickDesc': 'Removes unverified users when timeout expires',
    'verification.publishEmbed': 'Publish verification embed',
    'verification.save': 'Save changes',
    'verification.savedToast': 'Configuration saved!',
    'verification.publishedToast': 'Verification embed published successfully!',
    'verification.publishError': 'Failed to publish verification embed',
    'verification.providerHelp': 'For hCaptcha/Turnstile backend must be configured with site key + secret.',
    'verification.selectChannel': 'Select verification channel',
    'verification.selectRoleVerified': 'Select verified role',
    'verification.selectRoleUnverified': 'Select unverified role',
    'verification.selectProvider': 'Select provider',
    'verification.noneChannel': 'No channel',
    'verification.noneRole': 'No role',

    'servers.loading': 'Loading servers...',
    'servers.error': 'Error loading servers',
    'servers.retry': 'Retry',
    'servers.title': 'My servers',
    'servers.logout': 'Logout',
    'servers.noServers': 'No servers available',
    'servers.noServersDesc': 'You must be an owner or have administrator permissions to manage a server.',
    'servers.onlyBot': 'Only servers with bot',
    'servers.showAll': 'Show all',
    'servers.welcome': 'Welcome, {{username}}!',
    'servers.selectPrompt': 'Select a server to start managing plugins',
    'servers.members': '{{count}} members',

    'management.title': 'Server management',
    'management.server': 'Server',
    'management.toggleUpdated': 'Plugin status updated',
    'management.toggleError': 'Plugin update error',
    'management.loading': 'Loading...',
    'management.enable': 'Enable',
    'management.disable': 'Disable',
    'management.configure': 'Configure',
    'management.enabled': 'Enabled',
    'management.disabled': 'Disabled',
    'management.all': 'All',
    'management.overview': 'Overview',
    'management.overviewDesc': 'View general statistics and plugin status for the server',
    'management.welcomeDesc': 'Custom welcome messages for new members',
    'management.acceptedDesc': 'Approve/decline new members with pending dashboard and analytics',
    'management.verificationDesc': 'Captcha + multi-method verification',

    'welcome.loadError': 'Plugin loading error',
    'welcome.unableLoad': 'Unable to load data',
    'welcome.loadingConfig': 'Loading plugin configuration...',
    'welcome.title': 'Welcome & Farewell',
    'welcome.subtitle': 'Welcome new members and track who leaves the server',
    'welcome.pluginStatus': 'Plugin status',
    'welcome.disabled': 'Plugin disabled',
    'welcome.disabledDesc': 'Enable the plugin to send welcome messages and auto roles',
    'welcome.enableNow': 'Enable now',
    'welcome.saveFailed': 'Save failed',
    'welcome.saveFailedDesc': 'An error occurred while saving. Check your connection.',
    'welcome.welcomeMessage': 'Welcome message',
    'welcome.sendOnJoin': 'Send message on join',
    'welcome.channel': 'Channel',
    'welcome.systemChannel': 'System channel',
    'welcome.autoRole': 'Auto role',
    'welcome.none': 'None',
    'welcome.messageContent': 'Message content',
    'welcome.sendAsEmbed': 'Send as embed',
    'welcome.embedTitle': 'Embed title',
    'welcome.embedColor': 'Embed color',
    'welcome.embedThumb': 'Embed thumbnail',
    'welcome.userAvatar': 'User avatar',
    'welcome.serverIcon': 'Server icon',
    'welcome.noThumb': 'None',
    'welcome.embedBanner': 'Embed banner',
    'welcome.showServerBanner': 'Show server banner',
    'welcome.customBanner': 'Custom banner',
    'welcome.bannerReminder': 'Remember: the banner will be used only after saving the configuration.',
    'welcome.bannerError': 'Banner load error',
    'welcome.change': 'Change',
    'welcome.uploadBanner': 'Click to upload banner',
    'welcome.uploading': 'Uploading...',
    'welcome.uploadHint': 'PNG / JPG • max 5MB • recommended 1200×400',
    'welcome.farewellMessage': 'Farewell message',
    'welcome.sendOnLeave': 'Send message when a user leaves',
    'welcome.noPreview': 'No preview available. Upload a banner.',
    'welcome.uploadBannerBtn': 'Upload banner',
    'welcome.saving': 'Saving...',
    'welcome.save': 'Save changes',

    'support.helpCenter.title': 'Help Center',
    'support.helpCenter.subtitle': 'Quick guides and answers to the most common questions. Find FAQs, initial setup, and best practices here.',
    'support.helpCenter.quickGuide': 'Quick guide',
    'support.helpCenter.faq': 'FAQ',
    'support.helpCenter.discordSetup': 'Discord setup',
    'support.helpCenter.needSupport': 'Need support?',
    'support.helpCenter.needSupportDesc': 'Contact us or join the community for quick assistance.',
    'support.helpCenter.communityDiscord': 'Discord Community',
    'support.helpCenter.sendRequest': 'Send request',

    'support.contact.title': 'Contact Us',
    'support.contact.subtitle': 'Request personalized support for your community. We will get back to you as soon as possible.',
    'support.contact.name': 'Name',
    'support.contact.namePlaceholder': 'Your name',
    'support.contact.email': 'Email',
    'support.contact.subject': 'Subject',
    'support.contact.subjectPlaceholder': 'Request title',
    'support.contact.message': 'Message',
    'support.contact.messagePlaceholder': 'Describe your request',
    'support.contact.send': 'Send request',

    'support.bug.titlePage': 'Report a bug',
    'support.bug.subtitlePage': 'Help us improve by reporting issues and bugs you encountered.',
    'support.bug.pageModule': 'Page/Module',
    'support.bug.pageModulePlaceholder': 'Where you found the bug',
    'support.bug.description': 'Description',
    'support.bug.descriptionPlaceholder': 'Describe the issue',
    'support.bug.send': 'Report',

    'architect.updated': 'AI Server Architect updated',
    'architect.operationCompleted': 'Operation completed.',
    'architect.chatError': 'AI chat error',
    'architect.enableFirst': 'Enable the AI Server Architect plugin first',
    'architect.noBlueprint': 'No blueprint available',
    'architect.simulationCompleted': 'Simulation completed',
    'architect.appliedToDiscord': 'Changes applied to Discord',
    'architect.streamError': 'Apply stream error',
    'architect.applyStreamingError': 'Apply streaming error',
    'architect.subtitle': 'Gemini AI agent to create, fix, and optimize Discord servers from scratch.',
    'architect.chatTitle': 'AI Chat',
    'architect.chatDesc': 'Describe the server you want and the agent will generate the full blueprint with Gemini.',
    'architect.goalsPlaceholder': 'Goals: gaming community, staff hierarchy, support tickets, onboarding...',
    'architect.destructiveTitle': 'Destructive mode',
    'architect.destructiveDesc': 'Allows removing roles/channels/categories not included in the plan',
    'architect.emptyChat': 'Write a request: “create a full server with onboarding, staff roles, support, and logs”.',
    'architect.ai': 'AI',
    'architect.you': 'You',
    'architect.inputPlaceholder': 'Example: create a full server for SaaS support in English',
    'architect.liveTitle': 'Live Changes',
    'architect.liveDesc': 'Real-time view of the server structure and change plan generated by the agent.',
    'architect.roles': 'Roles',
    'architect.categories': 'Categories',
    'architect.channels': 'Channels',
    'architect.plannedChanges': 'Planned changes',
    'architect.refreshLive': 'Refresh live',
    'architect.simulate': 'Simulate',
    'architect.applyDiscord': 'Apply to Discord',
    'architect.liveApplyLog': 'Live apply log',
    'architect.noLiveEvents': 'No live events yet.',
    'architect.planTitle': 'Change plan',
    'architect.overwrites': 'Overwrites',
    'architect.resultSimulation': 'Simulation result',
    'architect.resultApplication': 'Application result',
    'architect.summary': 'Roles created {{rolesCreated}}, updated {{rolesUpdated}}, deleted {{rolesDeleted}} • Categories created {{categoriesCreated}}, deleted {{categoriesDeleted}} • Channels created {{channelsCreated}}, deleted {{channelsDeleted}}',

    'botPersonalizer.saved': 'Configuration saved!',
    'botPersonalizer.unavailable': 'Plugin not available for this server.',
    'botPersonalizer.subtitle': 'Bot name, status, and presence.',
    'botPersonalizer.nickname': 'Nickname (server)',
    'botPersonalizer.status': 'Status',
    'botPersonalizer.selectStatus': 'Select status',
    'botPersonalizer.idle': 'Idle',
    'botPersonalizer.dnd': 'Do Not Disturb',
    'botPersonalizer.activity': 'Activity',
    'botPersonalizer.activityPlaceholder': 'streaming on...',
    'botPersonalizer.save': 'Save changes',

    'customCommands.subtitle': 'Create custom commands for the server.',
    'birthdays.saved': 'Configuration saved!',
    'birthdays.subtitle': 'Celebrate member birthdays with automatic announcements.',
    'birthdays.announcementSettings': 'Announcement settings',
    'birthdays.announcementChannel': 'Announcement channel',
    'birthdays.selectAnnouncementChannel': 'Select birthday announcement channel',
    'birthdays.noChannel': 'No channel',
    'birthdays.announcementChannelHelp': 'Channel where birthday announcements will be posted.',
    'birthdays.announcementMessage': 'Announcement message',
    'birthdays.announcementMessagePlaceholder': 'Use {user} for mention, {age} for age',
    'birthdays.announcementMessageHelp': 'Use {user} to mention the user, {age} for their age.',
    'birthdays.announcementTime': 'Announcement time',
    'birthdays.reminderDaysBefore': 'Reminder days before',
    'birthdays.sameDay': 'Same day only',
    'birthdays.oneDayBefore': '1 day before',
    'birthdays.threeDaysBefore': '3 days before',
    'birthdays.oneWeekBefore': '1 week before',
    'birthdays.pingUser': 'Ping user on birthday',
    'birthdays.pingUserDesc': 'Mention the user in the announcement',
    'birthdays.showAge': 'Show age',
    'birthdays.showAgeDesc': 'Display member age in the announcement',
    'birthdays.privateReminders': 'Private reminders',
    'birthdays.privateRemindersDesc': 'Send DM reminders to birthday members',
    'birthdays.availableCommands': 'Available commands',
    'birthdays.setBirthday': 'Set your birthday',
    'birthdays.checkBirthday': 'Check someone\'s birthday',
    'birthdays.upcomingBirthdays': 'View upcoming birthdays in the server',
    'birthdays.saving': 'Saving...',
    'birthdays.save': 'Save configuration',
    'recording.updated': 'Voice Recording plugin updated',
    'recording.updateError': 'Voice Recording plugin update error',
    'recording.configSaved': 'Configuration saved',
    'recording.configSaveError': 'Failed to save configuration',
    'recording.recordingLive': 'Recording...',
    'recording.authRequired': 'Authentication required',
    'recording.downloadError': 'Failed to download',
    'recording.downloaded': 'Transcription downloaded',
    'recording.downloadFailed': 'Failed to download transcription',
    'recording.deleted': 'Recording deleted',
    'recording.deleteFailed': 'Failed to delete recording',
    'recording.title': 'Voice Recording',
    'recording.subtitle': 'Record voice channels and transcribe with Gemini AI',
    'recording.settings': 'Settings',
    'recording.recordings': 'Recordings',
    'recording.geminiTitle': 'Gemini API configuration',
    'recording.geminiDesc': 'Configure Gemini for voice-to-text transcription with speaker diarization',
    'recording.geminiKey': 'Gemini API Key',
    'recording.geminiHelp': 'Required for speech-to-text transcription. Get your key from Google AI Studio.',
    'recording.speakerDiarization': 'Speaker diarization',
    'recording.speakerDiarizationDesc': 'Identify and separate different speakers in the transcription',
    'recording.autoRecordTitle': 'Auto-record channels',
    'recording.autoRecordDesc': 'Select voice channels to automatically start recording when someone joins',
    'recording.noVoiceChannels': 'No voice channels found',
    'recording.autoStart': 'Auto-start recording',
    'recording.autoStartDesc': 'Automatically start recording when someone joins selected channels',
    'recording.storageTitle': 'Storage & retention',
    'recording.storageDesc': 'Configure how long to keep recordings and transcriptions',
    'recording.retentionDays': 'Retention period (days)',
    'recording.retentionHelp': 'Older recordings will be deleted automatically',
    'recording.saving': 'Saving...',
    'recording.save': 'Save configuration',
    'recording.recentTitle': 'Recent recordings',
    'recording.recentDesc': 'View and manage voice channel recordings',
    'recording.view': 'View',
    'recording.deleteConfirm': 'Delete this recording?',
    'recording.participants': 'participants',
    'recording.segments': 'segments',
    'recording.started': 'Started:',
    'recording.empty': 'No recordings yet. Use /start-recording in Discord to begin.',
    'recording.transcriptionTitle': 'Transcription',
    'recording.close': 'Close',
    'recording.staff': '👮 Staff',
    'recording.user': '👤 User',
    'recording.noTranscription': 'No transcription available yet. Make sure Gemini API key is configured.',
    'recording.howItWorks': 'How it works',
    'recording.step1Title': 'Configure channels',
    'recording.step1Desc': 'Select voice channels to monitor and enable auto-recording if desired',
    'recording.step2Title': 'Start recording',
    'recording.step2Desc': 'Use the /start-recording command or let auto-record handle it when users join',
    'recording.step3Title': 'AI transcription',
    'recording.step3Desc': 'Gemini API transcribes speech to text with speaker diarization',
    'recording.step4Title': 'View & download',
    'recording.step4Desc': 'Access transcriptions in the dashboard with staff/user labels and timestamps',
  },
};

function resolveInitialLocale(): Locale {
  const stored = localStorage.getItem('ui-locale');
  if (stored === 'it' || stored === 'en') return stored;

  const browserLang = String(navigator.language || '').toLowerCase();
  if (browserLang.startsWith('it')) return 'it';
  return 'en';
}

function interpolate(template: string, params: Record<string, string | number> = {}): string {
  return Object.entries(params).reduce((acc, [key, value]) => {
    return acc.split(`{{${key}}}`).join(String(value));
  }, template);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => resolveInitialLocale());

  const setLocale = (nextLocale: Locale) => {
    setLocaleState(nextLocale);
    localStorage.setItem('ui-locale', nextLocale);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale,
    t: (key: string, fallback?: string) => {
      const raw = translations[locale][key] || translations.en[key] || fallback || key;
      return raw;
    },
  }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }

  const tWithParams = (key: string, params?: Record<string, string | number>, fallback?: string) => {
    const base = ctx.t(key, fallback);
    if (!params) return base;
    return interpolate(base, params);
  };

  return {
    ...ctx,
    t: tWithParams,
  };
}
